import { ShieldCheck, MapPin, PawPrint, LockKey, User } from "@phosphor-icons/react";
import LegalPageShell from "@/components/LegalPageShell";
import { useI18n } from "@/context/I18nContext";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

export default function PrivacyPolicy() {
    const { t } = useI18n();
    return (
        <LegalPageShell
            testId="privacy-page"
            kicker={t("legal.privacy_kicker")}
            title={t("legal.privacy_title")}
            sub={t("legal.privacy_sub")}
            updated="February 2026"
            icon={<ShieldCheck size={28} weight="fill" />}
        >
            <p>
                At PawLink we believe safety starts with trust. This Privacy Policy explains the
                information we collect when you use PawLink, how we use it to help reunite lost
                pets with their families, and the controls you have over your data.
            </p>
            <p>
                <strong>The short version:</strong> we collect the minimum information required to
                make Smart QR pet IDs work. We never sell your data. Your home address is never
                shown to a finder. You are in control at every step.
            </p>

            <h2><User size={18} weight="fill" className="inline-block me-2 -mt-1 text-paw-forest dark:text-paw-terra" />What we collect about you</h2>
            <ul>
                <li><strong>Account details</strong> — name, email address, profile photo (imported from Google Sign-In), and, optionally, a phone number and country you choose to add.</li>
                <li><strong>Contact preferences</strong> — the language, appearance, and notification settings you pick inside PawLink.</li>
                <li><strong>Sign-in metadata</strong> — a session token, timestamps, and (for password accounts) a hashed password. We never store passwords in plain text.</li>
            </ul>

            <h2><PawPrint size={18} weight="fill" className="inline-block me-2 -mt-1 text-paw-forest dark:text-paw-terra" />Pet information you provide</h2>
            <ul>
                <li><strong>Basic profile</strong> — pet name, species, breed, photo, and a short introduction.</li>
                <li><strong>Safety details</strong> — medical notes, allergies, and a voice message you record for finders.</li>
                <li><strong>Owner message</strong> — the phrase a finder sees when they scan your Smart QR.</li>
                <li><strong>Home city and country</strong> — used for context and never shown as a full street address on the public profile.</li>
            </ul>

            <h2><MapPin size={18} weight="fill" className="inline-block me-2 -mt-1 text-paw-forest dark:text-paw-terra" />QR scan data</h2>
            <p>
                When a stranger scans your Smart QR, PawLink records the scan so we can alert you.
                We collect:
            </p>
            <ul>
                <li>The <strong>timestamp</strong> and <strong>city / country</strong> the scan came from.</li>
                <li><strong>Precise GPS coordinates</strong> — only if the finder grants location permission. If they decline, we fall back to an approximate area based on their IP address.</li>
                <li><strong>Optional message and phone number</strong> the finder chooses to share.</li>
            </ul>
            <p>
                Scan data is only visible to you, the pet&rsquo;s owner. Finders do not see your identity,
                your address, or previous scans.
            </p>

            <h2><LockKey size={18} weight="fill" className="inline-block me-2 -mt-1 text-paw-forest dark:text-paw-terra" />Location and address privacy</h2>
            <p>
                A finder scanning your Smart QR sees only what you have chosen to publish: pet photo,
                name, pet details, and a way to reach you (phone or SMS). Your home address is stored
                privately and never displayed on the public scan page. City and country appear only
                as broad context.
            </p>

            <h2>How we use your data</h2>
            <ul>
                <li>To operate the Smart QR service — creating, updating, and displaying pet profiles.</li>
                <li>To send you a real-time alert when someone scans your pet&rsquo;s tag.</li>
                <li>To personalize the experience (language, appearance, dashboard).</li>
                <li>To detect abuse, spam, or fraudulent scans.</li>
                <li>To process PawLink Protection subscriptions through our payment provider.</li>
            </ul>
            <p>
                We do not sell, rent, or share your personal information with third parties for
                advertising.
            </p>

            <h2>Your controls</h2>
            <ul>
                <li><strong>Edit or hide any pet</strong> at any time from the My Pets page.</li>
                <li><strong>Manage what a finder sees</strong> per pet — photo, message, voice, phone number.</li>
                <li><strong>Turn notifications on or off</strong> from the Account page.</li>
                <li><strong>Export or delete your account</strong> — the &ldquo;Delete account&rdquo; button in Account permanently removes your profile, pets, scan history, and messages.</li>
                <li><strong>Request access, correction, or portability</strong> of your personal data by emailing <a href={supportMailto({ subject: "PawLink Support Request — Data Access" })}>{SUPPORT_EMAIL}</a>.</li>
            </ul>

            <h2>Data retention</h2>
            <p>
                Account and pet data are retained while your PawLink account is active. Scan history
                is retained so you can consult it during a rescue; you can clear individual scans at
                any time. When you delete your account, all associated data is removed within 30 days
                from our production databases and backups.
            </p>

            <h2>Cookies and analytics</h2>
            <p>
                PawLink uses a single essential session cookie to keep you signed in. We do not use
                third-party advertising trackers. Basic anonymous usage analytics may be collected
                to improve reliability, always aggregated and never tied to individual pet profiles.
            </p>

            <h2>Contact</h2>
            <p>
                Questions about your data? Email us at <a href={supportMailto({ subject: "PawLink Support Request — Privacy" })}>{SUPPORT_EMAIL}</a>
                {" "}or use the <a href="/support">Contact Support</a> page.
            </p>
        </LegalPageShell>
    );
}
